import * as ToastPrimitives from "@radix-ui/react-toast";
import { AccountInterface, Channel, EscalationInterface, EscalationStatus, NotificationInterface } from "@swivl-mono/types";
import { motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import NotificationsModel from "../../Models/Notifications/Notifications.Model";
import { UsersModel } from "../../Models/Users/Users.Model";

import { ToastWrapper } from "./ToastWrapper";
import { Member } from "../../Models/Member/Member.Current";
import { warn } from "@swivl-mono/helpers";
import CallModel, { CallBarPosition } from "../../Models/CallModel/Call.Model";

interface BaseToastRootProps {
  account?: AccountInterface;
}
export interface ToastData {
  id: string;
  createdAt: Date;
  notification?: NotificationInterface;
  escalation?: EscalationInterface;

}

const SoundURL = 'https://swivl-cdn.s3-us-west-1.amazonaws.com/notification_sound.mp3'
export class SoundLooper {

  private static intervalId: number | null = null;
  private static audio: HTMLAudioElement | null = null;

  /**
   * Start playing the sound immediately and loop it every 5 seconds.
   * @param soundUrl URL of the sound file (mp3, wav, etc.)
   */
  static start() {
    // if already playing, don't restart
    if (this.intervalId !== null) return;

    this.audio = new Audio(SoundURL);

    // play immediately
    this.playSound();

    // then play every 5 seconds
    this.intervalId = window.setInterval(() => {
      this.playSound();
    }, 5000);
  }

  /**
   * Stop playing the sound.
   */
  static stop() {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audio = null;
    }
  }

  private static playSound() {
    if (!this.audio) return;
    // rewind to start in case it's still mid-play
    this.audio.currentTime = 0;
    this.audio.play().catch((err) => {
      console.error("Error playing sound:", err);
    });
  }
}

const BaseToastRoot = ({ account }: BaseToastRootProps) => {

  const { callBarPosition } = CallModel.useState((state) => state);

  const { activeNotifications } = NotificationsModel.useState((state) => state);
  // Filter out voice notifications. We use the call bar for all voice escalations


  const { unacknowledgedEscalations } = UsersModel.useState((state) => state);
  const hasAssignedEscalations = unacknowledgedEscalations?.some((item) => item.resolved === false && item.status === EscalationStatus.ASSIGNED && item.assignedMemberId === Member.id);

  useEffect(() => {

    if (hasAssignedEscalations) {
      SoundLooper.start()

    } else if (!hasAssignedEscalations) {
      SoundLooper.stop();
    }


  }, [hasAssignedEscalations])
  const layoutInfo = useMemo(() => {
    const toastHeight = 74;
    const margin = 9.4;
    // nav bar, padding, and right-positioned voice call bar heights
    const excessHeight = 90 + (callBarPosition !== CallBarPosition.Hidden && callBarPosition === CallBarPosition.BottomRight ? 90 : 0);
    const maxHeight = document.documentElement.clientHeight - excessHeight;

    let data: ToastData[] = [];
    if (activeNotifications) {
      data = activeNotifications.map((notification) => {
        return {
          id: "note" + notification.id,
          createdAt: notification.createdAt,
          notification,
        }
      });
    }
    if (unacknowledgedEscalations) {
      for (const escalation of unacknowledgedEscalations) {

        data.push({
          id: "escalation" + escalation.id,
          createdAt: escalation.createdAt,
          escalation,
        })

      }
    }
    data.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

    const toastsHeight = data && data.length * (toastHeight + margin) || 0;
    const overflowingIndex = toastsHeight > maxHeight
      ? Math.floor((maxHeight - margin) / (toastHeight + margin)) - 1
      : data?.length || 0;

    return {
      overflowingIndex,
      maxHeight,
      toastHeight,
      margin,
      toastsToRender: data?.slice(0, overflowingIndex).reverse() as ToastData[] || [],
      stackingToasts: data?.slice(overflowingIndex, overflowingIndex + 2).reverse() as ToastData[] || [],
    }
  }, [activeNotifications, unacknowledgedEscalations, callBarPosition]);

  return (
    <ToastPrimitives.Provider>
      <ToastPrimitives.Root
        duration={5000}
        open={Boolean(account) && layoutInfo.stackingToasts.length > 0}
        style={{ marginBottom: layoutInfo.stackingToasts.length > 0 ? `${(layoutInfo.stackingToasts.length * 5) + 80}px` : "80px" }}
      >
        <div className="relative">
          {layoutInfo.stackingToasts.map((data, index) => (
            <ToastWrapper
              key={data.id}
              style={{
                position: "absolute",
                top: index === 0 ? 0 : `${index * 6}px`,
                zIndex: 100 - index
              }}
              data={data}
            />
          ))}
        </div>
      </ToastPrimitives.Root>
      {layoutInfo.toastsToRender.map((data: ToastData, index: number) => (
        <ToastPrimitives.Root
          key={data.id}
          duration={5000}
          open={Boolean(account) && layoutInfo.toastsToRender.length > 0}
        >
          <ToastWrapper data={data} />
        </ToastPrimitives.Root>
      ))}
      <motion.div
        initial={false}
        animate={{ visibility: layoutInfo.toastsToRender.length > 0 ? "visible" : "hidden", y: callBarPosition !== CallBarPosition.Hidden && callBarPosition === CallBarPosition.BottomRight ? -82 : 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed right-0 bottom-0 z-50 w-1/3 p-4"
      >
        <ToastPrimitives.Viewport
          className="space-y-2 overflow-hidden shadow-lg flex-col-reverse"
          style={{ maxHeight: layoutInfo.maxHeight }}
        />
      </motion.div>
    </ToastPrimitives.Provider>
  )
}

export const ToastRoot = BaseToastRoot
